import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { StaticContext } from "react-router";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";

import { useAuth } from "@contexts/auth/AuthProvider";
import { LANDING_PAGE } from "@utils/constantsEnv";

import { configureAuth } from "../../api";

type AuthRouteProps = {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
};

type ExtendedRouteComponentProps = RouteComponentProps<
  {
    [x: string]: string | undefined;
  },
  StaticContext,
  unknown
>;

const AuthRoute: React.FC<AuthRouteProps> = ({ component: Component, ...rest }) => {
  const dispatch = useDispatch();
  const { isAuth, handleSessionExpired } = useAuth();

  const handleAuthenticatedRoute = (
    props: ExtendedRouteComponentProps,
    path: string
  ): React.ReactNode => {
    if (path === "/") {
      return <Redirect to="/dashboard" />;
    }

    return <Component {...props} />;
  };

  const handleUnauthenticatedRoute = (
    props: ExtendedRouteComponentProps,
    path: string
  ): React.ReactNode => {
    if (path === "/") {
      return <>Redirecting...{window.location.replace(LANDING_PAGE ?? "")}</>;
    }

    if (path !== "/consultation") {
      return <Redirect to="/login" />;
    }

    return <Component {...props} />;
  };

  const allowedComponent = (props: ExtendedRouteComponentProps): React.ReactNode => {
    if (!isAuth) {
      return handleUnauthenticatedRoute(props, rest.path);
    }
    return handleAuthenticatedRoute(props, rest.path);
  };

  // Initialize auth handlers once
  useEffect(() => {
    configureAuth({
      handleSessionExpired,
    });
  }, [dispatch]);

  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps) =>
        allowedComponent(props as ExtendedRouteComponentProps)
      }
    />
  );
};

export default AuthRoute;
